// src/utils/eventLogger.js
import { addDoc, collection, serverTimestamp, writeBatch, doc } from "firebase/firestore";

// Minimal, dependency-free UUID
function uuid4() {
  return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

const DEFAULTS = {
  flushIntervalMs: 5000,  // flush every 5s
  maxBatchSize: 20,       // or when 20 events queued
  ttlDays: 30,            // auto-delete after 30 days (configure TTL on 'expireAt')
};

let _db = null;
let _getUser = () => null;
let _appVersion = "0.0.0";
let _sessionId = uuid4();
let _queue = [];
let _timer = null;
let _config = { ...DEFAULTS };
let _logsColPath = "logs"; // flat collection for easy BigQuery export later if needed

export function initEventLogger({
  db,
  getUser,            // function that returns current user or null (e.g., () => auth.currentUser)
  appVersion = "0.0.0",
  ttlDays = DEFAULTS.ttlDays,
  flushIntervalMs = DEFAULTS.flushIntervalMs,
  maxBatchSize = DEFAULTS.maxBatchSize,
  logsCollection = "logs",
} = {}) {
  _db = db;
  _getUser = typeof getUser === "function" ? getUser : () => null;
  _appVersion = appVersion;
  _config = { ttlDays, flushIntervalMs, maxBatchSize };
  _logsColPath = logsCollection;

  // Auto flush on interval
  if (_timer) clearInterval(_timer);
  _timer = setInterval(flushEvents, _config.flushIntervalMs);

  // Flush when tab goes away
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushEvents({ blocking: true });
  });
  window.addEventListener("beforeunload", () => flushEvents({ blocking: true }));
}

export function setSessionId(sessionId) {
  _sessionId = sessionId || uuid4();
}

export function logEvent(type, data = {}) {
  if (!_db) return; // not initialised yet
  const user = safeUser(_getUser());
  const now = Date.now();
  const expireAt = new Date(now + (_config.ttlDays * 24 * 60 * 60 * 1000));

  _queue.push({
    __ts_client: now,          // useful when serverTimestamp hasn’t resolved
    ts: serverTimestamp(),     // authoritative time
    expireAt,                  // for Firestore TTL policy
    type,                      // e.g., "video_load"
    uid: user?.uid ?? null,
    sessionId: _sessionId,
    appVersion: _appVersion,
    data: sanitizeData(data),  // keep payload small & serializable
    ua: navigator.userAgent,   // optional, remove if you prefer
  });

  if (_queue.length >= _config.maxBatchSize) {
    flushEvents().catch(() => {}); // best-effort
  }
}

export function logError(err, context = {}) {
  const payload = {
    message: err?.message || String(err),
    name: err?.name,
    stack: (err && err.stack) ? String(err.stack).slice(0, 4000) : undefined,
    ...context,
  };
  logEvent("error_client", payload);
}

// --- internals ---

function sanitizeData(obj) {
  try {
    // Avoid huge nested objects; drop functions, undefined, and very long strings
    return JSON.parse(JSON.stringify(obj, (_k, v) => {
      if (typeof v === "function" || typeof v === "undefined") return null;
      if (typeof v === "string" && v.length > 500) return v.slice(0, 500);
      return v;
    }));
  } catch {
    return { note: "unserializable data dropped" };
  }
}

function safeUser(u) {
  if (!u) return null;
  // Only keep non-PII fields you need
  return { uid: u.uid, emailVerified: !!u.emailVerified };
}

export async function flushEvents({ blocking = false } = {}) {
  if (!_db || _queue.length === 0) return;
  const items = _queue.splice(0, _config.maxBatchSize);

  try {
    // Use a batch for fewer writes overhead
    const batch = writeBatch(_db);
    items.forEach((evt) => {
      const ref = doc(collection(_db, _logsColPath));
      batch.set(ref, evt);
    });
    await batch.commit();
  } catch (e) {
    // Put items back on failure (avoid loss)
    _queue = items.concat(_queue);
    if (blocking) {
      // Last-ditch attempt: write individually
      while (_queue.length) {
        const evt = _queue.shift();
        try {
          await addDoc(collection(_db, _logsColPath), evt);
        } catch {
          // swallow; we're unloading
          break;
        }
      }
    }
  }
}
